/**
 * A sentence a child can read, never the SDK's.
 *
 * `AuthContext.login` calls `signInWithEmailAndPassword` directly, so a wrong password
 * throws a `FirebaseError` whose `.message` is literally
 * `"Firebase: Error (auth/invalid-credential)."`. `Login.tsx` passed that straight to
 * `getErrorMessage()` — which returns `error.message` verbatim — and rendered it. So the
 * login form told every visitor which backend the product runs on, and told a
 * nine-year-old who mistyped their password something they cannot act on.
 *
 * Found 2026-09-10 by the E2E suite, on its first run against a deployed environment. The
 * test (`01-authentication.spec.ts:359`) has existed all along and asserts the error
 * contains no "firebase", "database" or "sql" — it could never run before, because the
 * suite had no environment to run against.
 *
 * `authService` has a translation table for exactly this and it is DEAD on the login path:
 * S135 records that `authService.signIn` is called by nothing while `Login` uses the
 * context. Rather than move the login path onto the unused service — a larger change with
 * its own risk — the table lives here, where both can reach it.
 *
 * The messages deliberately do NOT distinguish "no such account" from "wrong password".
 * Firebase stopped doing so itself (`auth/invalid-credential` replaced the split codes)
 * because the distinction is an account-enumeration oracle: it tells an attacker which
 * addresses are registered. The older codes are mapped to the same sentence.
 */

/** Messages the app authored deliberately, which must pass through unchanged. */
const APP_AUTHORED = /^(Please |We |Your |This |No account|Too many|Incorrect|Invalid email or password)/;

const FRIENDLY: Record<string, string> = {
  'auth/invalid-credential': 'Invalid email or password. Please try again.',
  'auth/wrong-password': 'Invalid email or password. Please try again.',
  'auth/user-not-found': 'Invalid email or password. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again in a few minutes.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/requires-recent-login': 'Please sign in again to complete this action.',
  'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
  'auth/weak-password': 'Password is too weak. Please use at least 8 characters with mixed case and numbers.',
  'auth/operation-not-allowed': 'Sign-in is currently unavailable. Please contact support.',
  'auth/popup-closed-by-user': 'The sign-in window closed before it finished. Please try again.',
  'auth/cancelled-popup-request': 'The sign-in window closed before it finished. Please try again.',
};

const GENERIC = 'Something went wrong signing you in. Please try again.';

/** The `code` off an unknown thrown value, narrowed (rule 8d). */
function codeOf(error: unknown): string | undefined {
  if (error === null || typeof error !== 'object') return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function messageOf(error: unknown): string | undefined {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return undefined;
}

/**
 * Turn anything thrown by an auth call into something safe to render.
 *
 * Order matters: a known code wins, then a message the app wrote itself, then the generic
 * sentence. Falling through to the raw message would defeat the whole point — so it never
 * does.
 */
export function friendlyAuthMessage(error: unknown): string {
  const code = codeOf(error);
  if (code && FRIENDLY[code]) return FRIENDLY[code];

  const message = messageOf(error);
  if (message && APP_AUTHORED.test(message) && !/firebase/i.test(message)) {
    return message;
  }

  return GENERIC;
}
